import useIsPrescriber from '@cannect/hooks/useIsPrescriber'
import useMediaQuery from '@cannect/hooks/useMediaQuery'
import { useTokenApi } from '@cannect/hooks/useTokenApi'
import { useQueryFilters } from '@cannect/hooks/utils/useQueryFilters'
import { useGetProductsFilters } from '@cannect/services/resources/products'
import { useMemo } from 'react'

import {
  FILTER_DISPLAY_ORDER,
  PRODUCT_FILTERS_CONFIG,
  SUPPLEMENT_CLASS_ID,
  TProductFilterType
} from '@cannect/constants/productFilters'
import { MOBILE_WIDTH } from '@cannect/utils/constants'

export const useProductFilters = () => {
  const { queryParams, updateQueryParams } = useQueryFilters<{
    supplier_id: string[]
    brand_id: string[]
    shape_id: string[]
    class_id: string[]
    category_id: string[]
    category_slug: string
    cbd_concentration: string[]
    thc_concentration: string[]
    cannabidiol_id: string[]
    cannabinoid_name: string[]
    proportion_id: string[]
    pathology: string[]
    veiculo: string[]
    extraction_method: string[]
    gmp: string[]
    curatorship_range: string[]
    min_price: string
    max_price: string
    delivery_by_association: string
    translated_name: string
  }>()

  const { tokenApi } = useTokenApi()
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const productsFiltersQuery = useGetProductsFilters()

  // Fonte única: registration_type === 2. Antes esta linha lia `prescriber_id`
  // direto do localStorage, divergindo do gate usado na classificação.
  const isPrescriberUser = useIsPrescriber()

  const filterOptions = useMemo(() => {
    if (!productsFiltersQuery.data)
      return {} as Record<TProductFilterType, Array<{ id: string; name: string; description?: string }>>

    const options: Record<TProductFilterType, Array<{ id: string; name: string; description?: string }>> = {} as any

    FILTER_DISPLAY_ORDER.forEach((filterType) => {
      // TODO Marketplace V2: Consertar tipagem
      const filterData = (productsFiltersQuery.data as any)[filterType]
      if (filterData && Array.isArray(filterData)) {
        options[filterType] = filterData.map((item: any) => ({
          id: item.id?.toString() || item.value?.toString(),
          name: item.name || item.label,
          description: item.description
        }))
      }
    })

    return options
  }, [productsFiltersQuery.data])

  const availableFilters = useMemo(() => {
    return FILTER_DISPLAY_ORDER.filter((filterType) => {
      const config = PRODUCT_FILTERS_CONFIG[filterType]

      if (config.isPrescriberOnly && !isPrescriberUser) {
        return false
      }

      const hasOptions = (filterOptions as any)[filterType] && (filterOptions as any)[filterType].length > 0
      if (!hasOptions) {
        return false
      }

      return true
    })
  }, [filterOptions, isPrescriberUser])

  const handleFilterChange = (
    filterKey: TProductFilterType | 'min_price' | 'max_price' | 'delivery_by_association' | 'category_slug' | 'translated_name' | 'cannabinoid_name',
    value: string
  ) => {
    if (filterKey === 'min_price' || filterKey === 'max_price') {
      updateQueryParams({ [filterKey]: value || undefined })
      return
    }

    if (filterKey === 'delivery_by_association') {
      const isActive = queryParams.get('delivery_by_association') === 'true'
      updateQueryParams({ delivery_by_association: isActive ? undefined : 'true' })
      return
    }

    if (filterKey === 'category_slug') {
      const current = queryParams.get('category_slug')
      updateQueryParams({ category_slug: current ? undefined : value })
      return
    }

    // cannabinoid_name: seleção única — substitui o canabinoide anterior (ou remove se clicar no mesmo)
    if (filterKey === 'cannabinoid_name') {
      const current = queryParams.get('cannabinoid_name')
      updateQueryParams({ cannabinoid_name: current === value ? [] : [value] })
      return
    }

    // translated_name: concatenar valores ao invés de array
    if (filterKey === 'translated_name') {
      const current = queryParams.get('translated_name') || ''
      const currentTerms = current.split(' ').filter(term => term.trim())

      let newTerms: string[]
      if (!currentTerms.includes(value)) {
        newTerms = [...currentTerms, value]
      } else {
        newTerms = currentTerms.filter((term) => term !== value)
      }

      updateQueryParams({ translated_name: newTerms.join(' ') || undefined })
      return
    }

    const currentFilters = queryParams.getAll(filterKey)

    let newFilters: string[]
    if (!currentFilters.includes(value)) {
      newFilters = [...currentFilters, value]
    } else {
      newFilters = currentFilters.filter((filter) => filter !== value)
    }

    const updateParams: any = { [filterKey]: newFilters }

    if (filterKey === 'supplier_id' && value === '27') {
      if (newFilters.includes('27')) {
        updateParams.supplier_id = newFilters.filter((id) => id !== '27')
        updateParams.class_id = [SUPPLEMENT_CLASS_ID]
      }
    }

    if (filterKey === 'brand_id') {
      const isAddingBrand = !currentFilters.includes(value)
      if (isAddingBrand) {
        updateParams.supplier_id = []
        updateParams.shape_id = []
        updateParams.class_id = []
        updateParams.category_id = []
        updateParams.category_slug = undefined
        updateParams.cbd_concentration = []
        updateParams.thc_concentration = []
        updateParams.cannabidiol_id = []
        updateParams.pathology = []
        updateParams.veiculo = []
        updateParams.extraction_method = []
        updateParams.cannabinoid_name = []
        updateParams.proportion_id = []
        updateParams.gmp = []
        updateParams.min_price = undefined
        updateParams.max_price = undefined
        updateParams.delivery_by_association = undefined
      }
    }

    updateQueryParams(updateParams)
  }

  const clearFilters = () => {
    updateQueryParams({
      supplier_id: [],
      brand_id: [],
      shape_id: [],
      class_id: [],
      category_id: [],
      category_slug: undefined,
      cbd_concentration: [],
      thc_concentration: [],
      cannabidiol_id: [],
      pathology: [],
      veiculo: [],
      cannabinoid_name: [],
      proportion_id: [],
      extraction_method: [],
      gmp: [],
      curatorship_range: [],
      min_price: undefined,
      max_price: undefined,
      delivery_by_association: undefined
    })
  }

  const isFilterOptionSelected = (filterKey: TProductFilterType | 'translated_name', value: string) => {
    // translated_name: verificar se o termo está na string concatenada
    if (filterKey === 'translated_name') {
      const current = queryParams.get('translated_name') || ''
      const terms = current.split(' ').filter(term => term.trim())
      return terms.includes(value)
    }

    return queryParams.getAll(filterKey).includes(value)
  }

  const activeFiltersCount = useMemo(() => {
    let count = 0
    availableFilters.forEach((filterKey) => {
      count += queryParams.getAll(filterKey).length
    })
    return count
  }, [availableFilters, queryParams])

  return {
    availableFilters,
    filterOptions,
    queryParams,

    isLoadingFilters: productsFiltersQuery.isLoading,
    isFetchingFilters: productsFiltersQuery.isFetching,

    isPrescriberUser,
    isMobile,
    isAuthenticated: !!tokenApi,

    activeFiltersCount,

    handleFilterChange,
    clearFilters,
    isFilterOptionSelected,

    filtersConfig: PRODUCT_FILTERS_CONFIG
  }
}
